/**
 * ImpersonationContext — lets an admin (DM) view the app "as" a player.
 *
 * When active the `useIdentity()` hook returns a synthesised AuthUser
 * whose sub/email match the impersonation target. Every page that cares
 * about "who am I" should read from useIdentity(); only auth-level
 * concerns (sign-out, admin route guards) should continue using
 * useAuth().user, which always returns the *real* logged-in user.
 *
 * The impersonation is purely client-side — the WebSocket and HTTP APIs
 * still authenticate with the DM's real token. The server sees the DM's
 * sub on every request, which is important for audit attribution.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthUser } from './auth';
import { isAdmin } from './auth';
import { useAuth } from './AuthContext';
import type { AdminUser } from './adminApi';

export interface ImpersonationTarget {
  sub: string;
  email: string;
  username: string;
}

interface ImpersonationContextValue {
  /**
   * The "effective" user. When impersonation is active this is a
   * synthesised AuthUser matching the target player; otherwise it's
   * the real authenticated user.
   */
  effectiveUser: AuthUser | null;
  /** The impersonation target, or null when not impersonating. */
  target: ImpersonationTarget | null;
  /** True when the real user is an admin and impersonation is active. */
  impersonating: boolean;
  /** Start impersonating a player. Only works for admins. */
  startImpersonating: (target: ImpersonationTarget) => void;
  /** Return to the real DM identity. */
  stopImpersonating: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextValue | undefined>(undefined);

/**
 * Build a synthetic AuthUser from an impersonation target. The groups
 * array is always empty — impersonated users are never admins — and
 * the username is derived from the email if no explicit username was
 * provided.
 */
function synthesiseUser(target: ImpersonationTarget): AuthUser {
  return {
    sub: target.sub,
    username: target.username || target.email.split('@')[0],
    email: target.email,
    groups: [], // impersonated player is never admin
  };
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [target, setTarget] = useState<ImpersonationTarget | null>(null);

  const admin = isAdmin(user);

  // If the real user signs out or loses admin, clear impersonation.
  const activeTarget = admin && target ? target : null;

  const effectiveUser = useMemo(() => {
    if (!user) return null;
    if (activeTarget) return synthesiseUser(activeTarget);
    return user;
  }, [user, activeTarget]);

  const startImpersonating = useCallback(
    (t: ImpersonationTarget) => {
      if (!admin) return;
      setTarget(t);
    },
    [admin],
  );

  const stopImpersonating = useCallback(() => {
    setTarget(null);
  }, []);

  const value = useMemo<ImpersonationContextValue>(
    () => ({
      effectiveUser,
      target: activeTarget,
      impersonating: activeTarget !== null,
      startImpersonating,
      stopImpersonating,
    }),
    [effectiveUser, activeTarget, startImpersonating, stopImpersonating],
  );

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}

/**
 * Hook for the "effective" identity. Use this anywhere the UI should
 * reflect the impersonated player when impersonation is active.
 */
export function useIdentity(): ImpersonationContextValue {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) {
    throw new Error('useIdentity must be used inside <ImpersonationProvider>');
  }
  return ctx;
}

/**
 * Helper to build an ImpersonationTarget from the AdminUser shape
 * returned by adminApi.listUsers().
 */
export function adminUserToTarget(u: AdminUser): ImpersonationTarget {
  return {
    sub: u.sub,
    email: u.email ?? '',
    username: u.email?.split('@')[0] ?? u.sub.slice(0, 8),
  };
}
